<template>
  <div>
    <!-- HEADER -->
    <page-header class="mt-6 lg:mt-0" :full-width="true">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full px-2 mb-6">
          <h1 slot="header" class="text-grey-darker text-xl font-bold leading-normal">{{ currentProperty.details.display_address }}</h1>
          <div class="w-full text-lg py-2"> {{ currentProperty.price_and_availability.listed_price  }}</div>
          <div class="flex w-full text-xs py-2 border-bottom">
            <span class="pr-8"><i class="fas fa-bed pr-2"></i> {{ currentProperty.property_specs.bedrooms }}</span>
            <span class="pr-8"><i class="fas fa-bath pr-2"></i> {{ currentProperty.property_specs.bathrooms }}</span>
            <span class="pr-8"><i class="fas fa-couch pr-2"></i> {{ currentProperty.property_specs.reception_rooms }}</span>
          </div>
        </div>
      </div>
    </page-header>

    <!-- NAVIGATION -->
    <loader v-if="loading"></loader>
    <button type="button"
      class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded mr-2 float-right" @click="backUrl()">
      <i class="fas fa-home"></i> Back to list
    </button>
    <tabs :tabHeaders="tabHeaders" :showIcon="true"  v-if="!loading">
      <!-- ICON -->
      <div slot="icon">
        <icon :name="'property_cms'" class="p-3" v-if="currentProperty.media.images.length === 0 || currentProperty.media.images[0].url === null"/>
        <div class="icon-img absolute pin" :style="{'background-image': `url(${currentProperty.media.images[0].url.large})`}" v-else></div>
      </div>

      <!-- Photos -->
      <div slot="tab0">
        <div class="flex flex-wrap -mx-2">

          <!-- CONTENT -->
          <div class="w-full px-2">
            <panel withHeader class="mb-5">
              <div class="flex flex-wrap -mx-2">
                <photo-gallery :cards="currentProperty.media.images" :type="'Image'" :media-type="0" :accept="'image/*'" :feed-provider="currentProperty.inbound_feed" :cardClass="'w-full md:w-1/2 lg:w-1/4 xl:1/3 p-2 mb-5'" v-if="currentProperty.media.images.length > 0" />
                <span v-else>No photo assets for this property</span>
              </div>
            </panel>
          </div>
        </div>
      </div>

      <!-- PROPERTY DESCRIPTION -->
      <div slot="tab1">
        <div class="flex flex-wrap -mx-2">

          <!-- CONTENT -->
          <div class="w-full px-2">
            <panel withHeader class="mb-5" v-if="currentProperty.description[currentLanguage].summary">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                * Main Summary
              </h1>

              <div class="flex flex-wrap -mx-2">

                <!-- DESCRIPTION TITLE -->
                <div class="w-full mb-5 px-2" v-if="currentProperty.description[currentLanguage].description_title">
                  <label>Title:</label>
                  <div class="mt-2">
                    <span>{{ currentProperty.description[currentLanguage].description_title }}</span>
                  </div>
                </div>

                <!-- MAIN SUMMARY -->
                <div class="w-full mb-5 px-2">
                  <div class="mt-2 text-sm" v-html="currentProperty.description[currentLanguage].summary">
                  </div>
                </div>
              </div>
            </panel>
            <panel withHeader class="mb-5" v-if="currentProperty.description[currentLanguage].description">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                * Full Description
              </h1>

              <div class="flex flex-wrap -mx-2">
                <!-- Full Description -->
                <div class="w-full mb-5 px-2">
                  <div class="mt-2 text-sm" v-html="currentProperty.description[currentLanguage].description">
                  </div>
                </div>
              </div>
            </panel>
          </div>
        </div>
      </div>

      <!-- PROPERTY FLOORPLAN -->
      <div slot="tab2">
        <div class="w-full px-2 flex-1 flex items-stretch">
          <panel withHeader class="mb-5 flex-1">
            <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
              Floorplans
            </h1>

            <div class="w-full px-2 flex-1 flex items-stretch">
              <photo-gallery :cards="currentProperty.media.floorplans" :type="'Floorplan'" :media-type="1" :accept="'image/*,application/pdf'" :feed-provider="currentProperty.inbound_feed" :cardClass="'lg:w-1/2 p-2 mb-5'" v-if="currentProperty.media.floorplans.length > 0" />
              <span v-else>No floorplan assets for this property</span>
            </div>
          </panel>
        </div>
      </div>
      <div slot="tab3">
        <div class="flex flex-wrap -mx-2">
          <!-- LOCATION -->
          <div class="w-full px-2">
            <panel withHeader class="mb-5">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Property Location
              </h1>
              <div class="w-full mb-8">
                <a class="button hover:border-grey-light m-2 h-10 py-3 px-6 text-sm border border-grey rounded text-black no-underline" @click="copyLink('https://www.google.com/maps?q=' + currentProperty.location.coords.lat + ',' + currentProperty.location.coords.lng)">Copy map URL<i class="fas fa-map-pin ml-2"></i></a>
                <a class="button hover:border-grey-light m-2 h-10 py-3 px-6 text-sm border border-grey rounded text-black no-underline" @click="copyLink(currentProperty.location.coords.lat + '/' + currentProperty.location.coords.lng)">Copy map Lat/Lng <i class="fas fa-copy ml-2"></i></a>
              </div>
              <!-- GOOGLE MAPS -->
              <GmapMap
                :center="currentProperty.location.coords"
                :zoom="15"
                :options="{disableDefaultUI: true, zoomControl: true}"
                map-type-id="roadmap"
                class="w-full h-screen mb-5"
              >
                <GmapMarker
                  :position="currentProperty.location.coords"
                  :clickable="true"
                  :draggable="true"
                  @dragend="mapPinMoved"
                />
              </GmapMap>
              <div class="flex w-full justify-center pb-4">
                <div class="mx-4 text-lg">Latitude: {{currentProperty.location.coords.lat}}</div>
                <div class="mx-4 text-lg">Longitude: {{currentProperty.location.coords.lng}}</div>
              </div>
            </panel>
          </div>
        </div>
      </div>
      <div slot="tab4">
        <div class="flex flex-wrap -mx-2">
          <!-- Other Media -->
          <!-- VIDEO -->
          <div class="w-full px-2 flex-1 flex items-stretch">
            <panel withHeader class="mb-5 flex-1">
              <h1 slot="header" class="border-b border-grey-lighter w-full pb-3 text-lg">
                Video
              </h1>
              <div class="flex flex-wrap -mx-2">
                <asset-video :media="currentProperty.media.videos" v-if="currentProperty.media.videos.length"></asset-video>
                <div v-else>No video assets for this property</div>
              </div>
              <h1 class="border-t border-grey-lighter w-full my-4 py-3 text-lg">
                Brochure
              </h1>
              <div v-if="currentProperty.media.documents.length">
                <div class="flex flex-wrap border-b border-grey-lighter pb-4" v-for="(document, index) in currentProperty.media.documents" :key="index">
                  <a class="button hover:border-grey-light h-10 pt-3 px-6 text-sm border border-grey rounded mr-2 text-black no-underline"
                     :href="document.url" :download="'document'+index+'.pdf'">Download file <i class="fas fa-download ml-2"></i></a>
                  <button type="button"
                          class="hover:border-grey-light h-10 py-2 px-6 text-sm border border-grey rounded mr-2"
                          @click="viewPDF(document.url)"
                  >View Brochure <i class="fas fa-book ml-2"></i></button>
                  <button type="button"
                          class="hover:border-grey-light h-10 py-2 px-6 text-sm border border-grey rounded mr-2"
                          @click="copyLink(document.url)"
                  >Copy Link <i class="fas fa-copy ml-2"></i></button>
                </div>
              </div>
              <div v-else>No brochure for this property</div>

            </panel>
          </div>
        </div>
      </div>
    </tabs>
  </div>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import ErrorBag from '@/ErrorBag'
import _ from 'lodash'
import DeletionModal from '@/components/Modals/DeletionModal'
import DateTimePicker from 'vue-vanilla-datetime-picker'

export default {
  name: 'PropertyAssetManagement',
  components: { DeletionModal, DateTimePicker },
  beforeRouteLeave (to, from, next) {
    if (!this.buttonPress && !_.isEqual(this.initialLoadedProperty, JSON.parse(JSON.stringify(this.currentProperty)))) {
      this.leaveModal = true
      this.destination = to
    } else {
      next()
    }
  },
  props: {
    selectedPropertyId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      initialLoadedProperty: null,
      propertyValueChanged: false,
      destination: null,
      buttonPress: false,
      answer: false,
      leaveModal: false,
      showDeleteModal: false,
      media: [{}],
      currentLanguage: 'en',
      currentRoom: null,
      currentProvince: null,
      currentCity: null,
      currentSuburb: null,
      moreSpecsOpen: false,
      agents: [],
      errorBag: new ErrorBag(),
      loading: true,
      saving: false,
      geocoding: false,
      summaryLength: 0,
      descriptionLength: 0,
      interval: null,
      errorCounts: {
        details: 0,
        description: 0,
        media: 0
      },
      initialOfficeId: null,
      feedLogVisible: false
    }
  },
  computed: {
    ...mapState(['currentProperty', 'propertyEnums', 'property24', 'selectedOffice']),
    default_description_title: function () {
      if (!this.currentProperty || !this.propertyEnums) {
        return ''
      }

      let output = []

      // Bedroom Count
      if (this.currentProperty.property_specs.bedrooms > 0) {
        output.push(this.currentProperty.property_specs.bedrooms + ' Bedroom')
      }

      // Property Type
      for (let i = 0; i < this.propertyEnums.type.length; i++) {
        for (let j = 0; j < this.propertyEnums.type[i].options.length; j++) {
          if (this.propertyEnums.type[i].options[j].value === this.currentProperty.property_specs.property_type) {
            output.push(this.propertyEnums.type[i].options[j].label)
          }
        }
      }

      // Department
      output.push(this.currentProperty.is_rental ? 'For Rent in' : 'For Sale in')

      // Town
      output.push(this.currentProperty.details.address_line3 ? this.currentProperty.details.address_line3 : this.currentProperty.details.address_town)

      return output.join(' ')
    },
    tabHeaders: function () {
      var tab = [
        {
          label: 'PHOTOS',
          badget: this.currentProperty.media.images
        },
        {
          label: 'DESCRIPTION'
        },
        {
          label: 'FLOORPLAN'
        },
        {
          label: 'MAP'
        },
        {
          label: 'OTHER MEDIA'
        }
      ]
      return tab
    }
  },
  watch: {
    selectedOffice: {
      handler: function () {
        console.log('change office', this.initialOfficeId, this.selectedOffice)
        if (this.initialOfficeId === null) {
          this.initialOfficeId = this.selectedOffice.id
          return
        }
        this.$router.replace(`/property-management`)
      },
      deep: true
    },
    currentProperty: function (newVal, oldVal) {
      if (newVal.id !== oldVal.id) {
        this.loadData()
      }
    }
  },
  methods: {
    ...mapActions(['displayToastNotification', 'setPageLoading', 'updateProperty', 'fetchProperty', 'fetchPropertyEnums', 'fetchPropertyAgents', 'fetchProperty24Provinces', 'fetchProperty24Cities', 'fetchProperty24Suburbs']),
    languageLabel: function (langCode) {
      for (let i = 0; i < this.propertyEnums.language.length; i++) {
        if (this.propertyEnums.language[i].value === langCode) {
          return this.propertyEnums.language[i].label
        }
      }
    },
    addRoom () {
      this.currentProperty.description[this.currentLanguage].rooms.push({
        name: '',
        dimension_text: '',
        description: ''
      })
      this.currentRoom = this.currentProperty.description[this.currentLanguage].rooms.length - 1
    },
    removeRoom () {
      this.currentProperty.description[this.currentLanguage].rooms.splice(this.currentRoom, 1)

      if (this.currentProperty.description[this.currentLanguage].rooms.length === 0) {
        this.currentRoom = null
      } else {
        this.previousRoom()
      }
    },
    previousRoom () {
      let nextRoom = this.currentRoom - 1
      if (nextRoom < 0) {
        nextRoom = this.currentProperty.description[this.currentLanguage].rooms.length - 1
      }
      this.currentRoom = nextRoom
    },
    nextRoom () {
      let nextRoom = this.currentRoom + 1
      if (nextRoom >= this.currentProperty.description[this.currentLanguage].rooms.length) {
        nextRoom = 0
      }
      this.currentRoom = nextRoom
    },

    addOpenHouse () {
      this.currentProperty.open_house.times.push({
        start: '',
        end: ''
      })
    },
    removeOpenHouse (i) {
      this.currentProperty.open_house.times.splice(i, 1)
    },
    agentToggled () {
      for (let i = 0; i < this.agents.length; i++) {
        if (this.agents[i].id === this.currentProperty.agents.primary && this.currentProperty.agents.assigned.indexOf(this.agents[i].id) >= 0) {
          return
        }
      }

      this.currentProperty.agents.primary = null
    },

    // START - SOUTH AFRICA
    countryChanged () {
      if (this.currentProperty.details.address_country === 'ZA') {
        this.currentProvince = {}
        this.currentCity = {}
        this.currentSuburb = {}
        this.fetchProperty24Provinces('ZA').then(() => {
          this.currentProvince = this.property24.provinces[0]
          this.currentProperty.details.address_line4 = this.currentProvince.name
          this.provinceChanged()
        }, () => {
          this.displayError('Error', 'Failed to retrieve province listing from Property24')
        })
      }
    },
    provinceChanged () {
      this.currentCity = {}
      this.currentSuburb = {}
      this.currentProperty.details.address_line4 = this.currentProvince.name
      this.fetchProperty24Cities(this.currentProvince.id).then(() => {
        this.currentCity = this.property24.cities[this.currentProvince.id][0]
        this.currentProperty.details.address_town = this.currentCity.name
        this.cityChanged()
      }, () => {
        this.displayError('Error', 'Failed to retrieve city listing from Property24')
      })
    },
    cityChanged () {
      this.currentSuburb = {}
      this.currentProperty.details.address_town = this.currentCity.name
      this.fetchProperty24Suburbs(this.currentCity.id).then(() => {
        this.currentSuburb = this.property24.suburbs[this.currentCity.id][0]
        this.currentProperty.details.address_line3 = this.currentSuburb.name
      }, () => {
        this.displayError('Error', 'Failed to retrieve suburb listing from Property24')
      })
    },
    suburbChanged () {
      this.currentProperty.details.address_line3 = this.currentSuburb.name
    },
    // END - SOUTH AFRICA
    isEmpty (value) {
      if (value) {
        value = value.replace(/<\/?p[^>]*>/g, '')
        value = value.trim()
      }
      return !value
    },
    removeEmptyLanguages () {
      for (let i = 0; i < this.propertyEnums.language.length; i++) {
        let code = this.propertyEnums.language[i].value

        // Do not remove English
        if (code === 'en' || !this.currentProperty.description[code]) {
          continue
        }

        // Remove from array if there's no value
        if (this.isEmpty(this.currentProperty.description[code].description_title) && this.isEmpty(this.currentProperty.description[code].summary) && this.isEmpty(this.currentProperty.description[code].features[0])) {
          delete this.currentProperty.description[code]
        }
      }
    },
    mapPinMoved (pin) {
      this.currentProperty.location.coords.lat = pin.latLng.lat()
      this.currentProperty.location.coords.lng = pin.latLng.lng()
    },
    initForm () {
      this.initVideo()

      // Default timezone
      if (this.currentProperty.open_house.times.length === 0) {
        this.currentProperty.open_house.timezone = 'Europe/London'
      }

      // Desctiption counts. Computed properties do not work for this... :(
      clearInterval(this.interval)
      this.interval = setInterval(() => {
        this.summaryLength = this.currentProperty.description[this.currentLanguage].summary.length
        this.descriptionLength = this.currentProperty.description[this.currentLanguage].description.length
      }, 500)

      // Display the first room if defined
      this.currentRoom = this.currentProperty.description[this.currentLanguage].rooms.length > 0 ? 0 : null

      this.loading = false
      this.setPageLoading(false)
      this.initialLoadedProperty = JSON.parse(JSON.stringify(this.currentProperty))
    },
    initVideo () {
      // Placeholder video media
      if (this.currentProperty.media.videos.length === 0) {
        this.currentProperty.media.videos.push({
          url: '',
          caption: 'Video',
          cms_created: true
        })
      }
    },
    displayError (title, message) {
      this.displayToastNotification({
        type: 'danger',
        icon: 'fa-times-circle',
        title: title,
        info: message
      })
    },
    loadData () {
      this.initialOfficeId = this.selectedOffice.id ? this.selectedOffice.id : null
      this.setPageLoading(true)
      let property = this.fetchProperty({ propertyId: this.selectedPropertyId, type: 'asset' })
      let propertyEnums = this.fetchPropertyEnums()
      let agents = this.fetchPropertyAgents(this.selectedPropertyId).then((response) => {
        this.agents = response
      })

      Promise.all([property, propertyEnums, agents]).then(() => {
        // Load initial South Africa address dropdowns
        if (this.currentProperty.details.address_country === 'ZA') {
          let province = this.currentProperty.details.address_line4
          let city = this.currentProperty.details.address_town
          let suburb = this.currentProperty.details.address_line3

          // No province. Likely a new property. Load normally.
          if (!province) {
            this.countryChanged()
            this.initForm()
            return
          }

          // Pre-select province
          this.fetchProperty24Provinces('ZA').then(() => {
            for (let i = 0; i < this.property24.provinces.length; i++) {
              if (this.property24.provinces[i].name === province) {
                this.currentProvince = this.property24.provinces[i]
                break
              }
            }

            if (!this.currentProvince) {
              this.currentCity = {}
              this.currentSuburb = {}
              this.currentProvince = {}
              this.displayError('Error', 'Invalid province. Please reselect.')
              this.initForm()
              return
            }

            // Pre-select city
            this.fetchProperty24Cities(this.currentProvince.id).then(() => {
              for (let i = 0; i < this.property24.cities[this.currentProvince.id].length; i++) {
                if (this.property24.cities[this.currentProvince.id][i].name === city) {
                  this.currentCity = this.property24.cities[this.currentProvince.id][i]
                  break
                }
              }

              if (!this.currentCity) {
                this.currentCity = {}
                this.currentSuburb = {}
                this.displayError('Error', 'Invalid city. Please reselect.')
                this.initForm()
                return
              }

              // Pre-select suburb
              this.fetchProperty24Suburbs(this.currentCity.id).then(() => {
                for (let i = 0; i < this.property24.suburbs[this.currentCity.id].length; i++) {
                  if (this.property24.suburbs[this.currentCity.id][i].name === suburb) {
                    this.currentSuburb = this.property24.suburbs[this.currentCity.id][i]
                    break
                  }
                }

                if (!this.currentSuburb) {
                  this.currentSuburb = {}
                  this.displayError('Error', 'Invalid suburb. Please reselect.')
                  this.initForm()
                  return
                }

                // Now init form
                this.initForm()
              }, () => {
                this.displayError('Error', 'Failed to retrieve suburb listing from Property24')
                this.initForm()
              })
            }, () => {
              this.displayError('Error', 'Failed to retrieve city listing from Property24')
              this.initForm()
            })
          }, () => {
            this.displayError('Error', 'Failed to retrieve province listing from Property24')
            this.initForm()
          })
        } else {
          this.initForm()
        }
      })
    },
    viewPDF (url) {
      window.open(url, '_blank')
    },
    copyLink (url) {
      const tempInput = document.createElement('input')
      tempInput.value = url
      document.body.appendChild(tempInput)
      tempInput.select()
      document.execCommand('copy')
      document.body.removeChild(tempInput)
      alert('URL copied to clipboard')
    },
    backUrl () {
      this.$emit('updateAssetComponentVisibility')
    }
  },
  created () {
    this.loadData()
  },
  beforeDestroy: function () {
    clearInterval(this.interval)
  }
}
</script>

<style lang="scss" scoped>
.icon-img {
  background-size: cover;
  background-position: center;
}
.mh-30 {
  max-height: 25px;
}
ul.list-none {
  list-style-type: none;
  padding-left: 25px;
  li {
    margin-bottom: 2rem;
    .fas {
      margin-left: -25px;
    }
  }
}
</style>

<style lang="scss">
.editor.property-cms-editor {
  .ProseMirror {
    min-height: 12rem;
  }
}
</style>
